import {
  type MutateOptions,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { gqlClient } from '@/lib';
import type { DeleteEventInput, EventFor, Exact } from '@/types/gql.generated';
import { isGQLErrorOfType, type QueryError } from '@/utils/errors';
import type {
  DeleteEventMutation,
  DeleteEventMutationVariables,
} from './useDeleteEvent.generated';

const query = gql`
  mutation DeleteEvent($input: DeleteEventInput!) {
    deleteEvent(input: $input)
  }
`;

export const useDeleteEvent = (
  eventFor: EventFor,
  options?: Pick<
    UseMutationOptions<
      DeleteEventMutation,
      QueryError,
      Exact<{ input: DeleteEventInput }>
    >,
    'onSuccess' | 'onError' | 'onSettled'
  >
) => {
  const { mutate, ...rest } = useMutation<
    DeleteEventMutation,
    QueryError,
    DeleteEventMutationVariables
  >({
    ...options,
    mutationFn: async (variables) => {
      try {
        return await gqlClient.request<
          DeleteEventMutation,
          DeleteEventMutationVariables
        >(query, variables);
      } catch (error) {
        // If the event is already gone on the server, deleting it again (e.g. a
        // double-click before the list refreshes) has already achieved the
        // desired end state. Treat it as a success so callers revalidate and
        // drop it from the UI instead of showing an error and leaving the stale
        // event in place, which just invites another failed delete (Sentry
        // WEB-CLIENT-25M).
        if (isGQLErrorOfType(error, 'EventNotFound')) {
          return { deleteEvent: null };
        }
        throw error;
      }
    },
  });

  const deleteEvent = useCallback(
    (
      { eventId }: { eventId: string },
      options?: MutateOptions<
        DeleteEventMutation,
        QueryError,
        DeleteEventMutationVariables
      >
    ) => {
      const input: DeleteEventInput = {
        eventId,
        for: eventFor,
      };
      mutate({ input }, options);
    },
    [mutate, eventFor]
  );

  return {
    ...rest,
    deleteEvent,
  };
};
